<script lang="ts" setup>
import InputSuffix from '@/components/InputSuffix.vue'
import projectStore from '@/store/ProjectStore'
import userStore from '@/store/UserStore'
import FormElement from '@/components/FormElement.vue'
import formStore from '@/store/FormStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { capitalizeFirstLetter } from '@/helper/tools'
import { FormGlazedSurface } from '@/helper/Types'
import FormController from '@/controllers/FormController'
import CalculationResultBadge from '@/components/steps/planning/CalculationResultBadge.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import i18n from '@/helper/Translations'
import ComboBox from '@/components/ComboBox.vue'
import { computed } from 'vue'

const props = defineProps<{
  validator: ValidatorType
  calculatedCoolingLoad?: string
}>()

defineExpose({
  validator: props.validator
})

defineEmits<{
  (e: 'updateCoolingLoad'): void
}>()

const windowOrientations = computed(() => {
  return userStore.windowOrientations.map(ortientation => ({
    label: i18n.global.t(`step_heating_details.orientations.${ortientation}`),
    value: ortientation
  }))
})

const coolingIndoorTemperatures = computed(() => {
  return userStore.coolingIndoorTemperatures.map(temperature => ({
    label: `${temperature} ${i18n.global.t('global.degree_celsius')}`,
    value: temperature
  }))
})

const ventilationTypes = computed(() => {
  return userStore.coolingVentilationTypes.map(ventilationType => ({
    label: i18n.global.t(`step_heating_details.ventilation_types.${ventilationType}`),
    value: ventilationType
  }))
})

function addSurfaceClicked(): void {
  setTimeout(() => {
    // set a small timeout so the button doesn't immediately slide away on click
    (formStore.data.glazedSurfaces as FormGlazedSurface[]).push(FormController.getEmptyGlacedSurface())
  }, 100)
}

function deleteSurfaceClicked(index: number): void {
  setTimeout(() => {
    // set a small timeout so the button doesn't immediately slide away on click
    (formStore.data.glazedSurfaces as FormGlazedSurface[]).splice(index, 1)
  }, 100)
}
</script>

<template>
  <section class="form__section has--line section--cooling-load-calculation">
    <div class="form__section-inner">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_cooling_calculation" />
      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.subtitle_windows') }}
        </h3>
        <div class="form__row">
          <div class="form__table options-table">
            <div class="tr">
              <div class="th column is--name">
                {{ $t('step_heating_details.table_window') }}
              </div>
              <div class="th column is--orientation">
                {{ $t('step_heating_details.table_orientation') }}
              </div>
              <div class="th column is--width">
                {{ $t('step_heating_details.table_width') }}
              </div>
              <div class="th column is--height">
                {{ $t('step_heating_details.table_height') }}
              </div>
              <div class="th column is--shading">
                {{ $t('step_heating_details.table_shading') }}
              </div>
              <div class="th column is--delete" />
            </div>
            <transition-group name="fade-expand">
              <div v-for="(surface, i) in formStore.data.glazedSurfaces" :key="surface.id" class="tr window-item">
                <div class="td column is--name">
                  <FormElement
                    v-validate="projectStore.projectProperties.glazedSurfaces.formValidationRule?.name"
                    :error="validator['surfaceName' + capitalizeFirstLetter(surface.id)]?.error"
                  >
                    <input
                      :id="'surface-name-' + surface.id"
                      v-model="surface.name"
                      :placeholder="$t('step_heating_details.placeholder_window_name')"
                      data-hj-suppress
                      type="text"
                      @input="$emit('updateCoolingLoad')"
                    >
                  </FormElement>
                </div>
                <div class="td column is--orientation">
                  <FormElement
                    v-validate="projectStore.projectProperties.glazedSurfaces.formValidationRule?.orientation"
                    :error="validator['surfaceOrientation' + capitalizeFirstLetter(surface.id)]?.error"
                  >
                    <ComboBox
                      :id="`surface-orientation-${surface.id}`"
                      v-model="surface.orientation"
                      :options="windowOrientations"
                      :placeholder="$t('step_heating_details.placeholder_window_orientation')"
                      @change="$emit('updateCoolingLoad')"
                    />
                  </FormElement>
                </div>
                <div class="td column is--width">
                  <FormElement
                    v-validate="projectStore.projectProperties.glazedSurfaces.formValidationRule?.width"
                    :error="validator['surfaceWidth' + capitalizeFirstLetter(surface.id)]?.error"
                  >
                    <InputSuffix
                      :id="`surface-width-${surface.id}`"
                      v-model="surface.width"
                      :placeholder="$t('step_heating_details.placeholder_window_width')"
                      suffix=" m"
                      @input="$emit('updateCoolingLoad')"
                    />
                  </FormElement>
                </div>
                <div class="td column is--height">
                  <FormElement
                    v-validate="projectStore.projectProperties.glazedSurfaces.formValidationRule?.height"
                    :error="validator['surfaceHeight' + capitalizeFirstLetter(surface.id)]?.error"
                  >
                    <InputSuffix
                      :id="`surface-height-${surface.id}`"
                      v-model="surface.height"
                      :placeholder="$t('step_heating_details.placeholder_window_height')"
                      suffix=" m"
                      @input="$emit('updateCoolingLoad')"
                    />
                  </FormElement>
                </div>
                <div class="td column is--shading">
                  <FormElement>
                    <label class="form__checkbox">
                      <input
                        :id="'shading-selected-' + i"
                        v-model="surface.shaded"
                        type="checkbox"
                        @input="$emit('updateCoolingLoad')"
                      >
                      <span class="form__cm" />
                      {{ $t('step_heating_details.label_window_shading') }}
                    </label>
                  </FormElement>
                </div>
                <div class="td column is--delete">
                  <button
                    :disabled="formStore.data.glazedSurfaces.length < 2"
                    class="icon-btn"
                    type="button"
                    @click="deleteSurfaceClicked(i)"
                  >
                    <i class="ic-trash" />
                  </button>
                </div>
              </div>
            </transition-group>
            <div class="tr">
              <div class="td column">
                <button
                  id="btn-add-surface"
                  class="btn is--tertiary has--icon-right"
                  type="button"
                  @click="addSurfaceClicked"
                >
                  {{ $t('step_heating_details.btn_add_surface') }} <i class="ic-plus" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.subtitle_living_area_information') }}
        </h3>

        <div class="row form__row has--large-space">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.coolingIndoorTemperature.formValidationRule"
              :error="validator.indoorTemp?.error"
              :label="$t('step_heating_details.label_indoor_temperature')"
              :required="projectStore.formPropertyIsRequired('coolingIndoorTemperature')"
            >
              <ComboBox
                id="indoor-temp"
                v-model="formStore.data.coolingIndoorTemperature"
                :options="coolingIndoorTemperatures"
                :placeholder="$t('step_heating_details.placeholder_indoor_temperature')"
                @change="$emit('updateCoolingLoad')"
              />
            </FormElement>
          </div>

          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.coolingVentilationType.formValidationRule"
              :error="validator.coolingVentilationType?.error"
              :required="projectStore.formPropertyIsRequired('coolingVentilationType')"
              label-key="step_heating_details.label_type_of_ventilation"
            >
              <ComboBox
                id="cooling-ventilation-type"
                v-model="formStore.data.coolingVentilationType"
                :options="ventilationTypes"
                :placeholder="$t('step_heating_details.placeholder_type_of_ventilation')"
                @change="$emit('updateCoolingLoad')"
              />
            </FormElement>
          </div>
        </div>
      </div>

      <CalculationResultBadge
        id="cooling-calc-badge"
        :badge-label="$t('step_heating_details.badge_calculated_cooling_load')"
        :calculated-load="calculatedCoolingLoad"
        icon-class="ic-air"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';
@import "@/assets/style/helpers/variables";

.section {
  &--cooling-load-calculation {
    .form__section-inner {
      padding-bottom: rem-calc(150px);
    }
  }
}

@include media('<tablet') {
  .options-table {
    overflow: hidden;
  }

  .window-item {
    flex-wrap: wrap;
    border-bottom: 1px solid $borderColorLight;
    padding: 1rem;
    gap: 1rem;

    .column {
      padding: 0;
      border-bottom: none;

      &:last-child {
        padding-right: 0;
      }
    }

    .is--shading {
      flex-basis: 80%;
      align-items: center;

      label {
        margin-top: 0;
      }
    }

    .is--delete {
      flex: 1;
      justify-content: flex-end;

      button {
        margin-top: 0;
      }
    }
  }
}
</style>
